import { Children, isValidElement, type ReactNode } from 'react';
import { EST_MARK, isEstimateWord, showsBuildLabels } from '@aithora/shared';
import { useOptionalConsoleRole } from '../session/SessionContext';

/**
 * Whether this screen draws build labels (SAMPLE, MODELED, MODELED EST., WHAT-IF, NOT LOGGED
 * YET, "trend MODELED"): Developer and Super Admin only, for the role on screen (so View as a
 * customer shows what the customer sees). Outside a session (a part rendered on its own) the
 * labels stay, as nobody is a customer there.
 */
export function useBuildLabels(): boolean {
  const role = useOptionalConsoleRole();
  return role === null ? true : showsBuildLabels(role);
}

/** The quiet "est." a customer sees right after a modeled or estimated number (Andrew 2026-10-15). */
export const EST_STYLE = 'display:inline;margin-left:4px;font-size:14px;font-weight:500;letter-spacing:0;text-transform:none;color:#54606C;white-space:nowrap;';
export function Est() {
  return (
    <span data-est="" style={{ marginLeft: 4, fontSize: 14, fontWeight: 500, letterSpacing: 0, textTransform: 'none', color: '#54606C', whiteSpace: 'nowrap' }}>
      {EST_MARK}
    </span>
  );
}

/** The plain text inside a label element ("MODELED" from <span><b>MODELED</b></span>). */
export function textOf(node: ReactNode): string {
  let out = '';
  Children.forEach(node, (c) => {
    if (typeof c === 'string' || typeof c === 'number') out += String(c);
    else if (isValidElement<{ children?: ReactNode }>(c)) out += textOf(c.props.children);
  });
  return out;
}

/**
 * Draws its children only where build labels show. Wrap every inline build label in it.
 * Everyone else sees "est." for a modeled or estimated label, and nothing for the rest.
 */
export function BuildLabel({ children }: { children: ReactNode }) {
  if (useBuildLabels()) return <>{children}</>;
  return isEstimateWord(textOf(children)) ? <Est /> : null;
}

/** The build-label words (a tag that is only one of these is a build label). */
const BUILD_WORD = /^(SAMPLE|MODEL|MODELED|MODELED EST\.|WHAT-IF|ESTIMATED|MEASURED|LIVE|RECORDED|PART RECORDED|PART MODEL|NOT WIRED|NOT LOGGED YET|BENCHMARK|OURS|VERIFIED|PUBLISHED|INDEX)\b/;
export const isBuildWord = (s: string | null | undefined): boolean => !!s && BUILD_WORD.test(s.trim());

/**
 * For a tag a page computes as a value (a tile's tag, a row's source chip): the tag where
 * build labels show, '' for a build word where they do not. Any other tag stays.
 */
export function useBuildTag(): <T extends string | null | undefined>(tag: T) => T | '' {
  const show = useBuildLabels();
  return (tag) => (!show && isBuildWord(tag) ? '' : tag);
}

/** Draws its children unless `tag` is a build word on a screen that hides build labels. */
export function BuildTagged({ tag, children, keepSlot }: { tag: string | null | undefined; children: ReactNode; keepSlot?: boolean }) {
  const show = useBuildLabels();
  // A modeled or estimated tag reads "est." for a customer (Andrew 2026-10-15).
  if (!show && isEstimateWord(tag)) return <Est />;
  // keepSlot: inside a grid row the hidden tag still holds its cell, so the columns after it
  // stay under their headers (the crawl found Performance's numbers one column off).
  if (!show && isBuildWord(tag)) return keepSlot ? <span aria-hidden="true" /> : null;
  return <>{children}</>;
}

/** "$4.2M · MODELED EST." -> "$4.2M": a text with its build words taken out. */
export function stripBuildWords(s: string): string {
  return s
    .replace(/^(MODELED EST\.|MODELED|SAMPLE|WHAT-IF)(:\s*|\s+)/, '')
    .replace(/\s*·\s*(MODELED EST\.|MODELED|MEASURED|SAMPLE|LIVE|WHAT-IF)(?=\s*(·|$))/g, '')
    .replace(/\s+(MODELED|MEASURED)(?=\s*·)/g, '');
}

/** For text that carries a build word inline: as written where build labels show, without it elsewhere. */
export function useBuildText(): (s: string) => string {
  const show = useBuildLabels();
  return (s) => (show ? s : stripBuildWords(s));
}

/**
 * Developer notes ("not served yet", route names such as GET /api/frameworks, "no route",
 * rule internals) follow the same rule as build labels: Developer and Super Admin read
 * them; a customer sees nothing in their place, or a plain "Not available yet".
 */
export const useDevNotes = useBuildLabels;

const DEV_MARK = /\b(not served|no route|route\b|routes\b|endpoint|GET \/|POST \/|PUT \/|PATCH \/|DELETE \/|\/api\/|not wired|NO FLAG|backfill)/i;
/** True for text that only makes sense to the people building Aithora. */
export const isDevNote = (s: string | null | undefined): boolean => !!s && DEV_MARK.test(s);

/** Plain words for a customer in place of a developer note. */
export const NOT_AVAILABLE = 'Not available yet';

/** For a text that may be a developer note: as written for Developer and Super Admin, else plain. */
export function useCustomerText(): (s: string, fallback?: string) => string {
  const dev = useDevNotes();
  return (s, fallback = NOT_AVAILABLE) => (dev || !isDevNote(s) ? s : fallback);
}

/** A developer note: drawn for Developer and Super Admin only (or `fallback` for everyone else). */
export function DevNote({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  return useDevNotes() ? <>{children}</> : <>{fallback}</>;
}
